import { IonContent, IonHeader, IonPage, IonTitle, IonToolbar, IonButton, IonList, IonItem, IonLabel, IonGrid, IonRow, IonCol, IonInput, IonModal, IonToast, IonAlert } from '@ionic/react';
import React, { useEffect, useState } from 'react';
import './TransferToFarm.css';
import { getPlotIdsOfFarm, transferFarm } from '../services/farm.service';
import UserList from '../pages/UserList';

interface IProps {
    farmId: string;
    farmUserId: string;
    onClose: Function;
    onTransferred: Function;
}

// Admin only: moves the whole farm, including all its plots, to another user
const TransferFarm: React.FC<IProps> = (props) => {
    const [newFarmUserId, setNewFarmUserId] = useState('');
    const [plotCount, setPlotCount] = useState<number | null>(null);
    const [showSelectUserModal, setShowSelectUserModal] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [onError, setOnError] = useState('');

    useEffect(() => {
        let mounted = true;
        getPlotIdsOfFarm(props.farmId)
            .then(plots => {
                if (mounted) {
                    setPlotCount(plots?.length ?? 0);
                }
            })
            .catch(() => undefined);
        return () => { mounted = false };
    }, [props.farmId]);

    function selectUser(item: any) {
        setNewFarmUserId(item.farmUserId);
        setShowSelectUserModal(false);
    }

    function onSubmit() {
        if (!newFarmUserId) {
            setOnError('Select the user to transfer the farm to');
            return;
        }
        // no same-owner check here: re-picking the current owner completes a partially failed transfer,
        // and the server rejects it when the farm and all its plots already belong to that user
        setShowConfirmation(true);
    }

    async function transfer() {
        setSubmitting(true);
        try {
            const response = await transferFarm(props.farmId, newFarmUserId);
            // the parent closes this modal, so it shows the success toast
            props.onTransferred(newFarmUserId, response.plotsTransferred);
        } catch (err: any) {
            setOnError(err?.message ?? 'Farm transfer failed');
        } finally {
            setSubmitting(false);
        }
    }

    const plotsText = plotCount === null ? 'all its plots' : `its ${plotCount} plot(s)`;

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Transfer Farm</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen>
                <IonList>
                    <IonItem>
                        <IonLabel position="stacked">Farm Id</IonLabel>
                        <IonInput readonly value={props.farmId}></IonInput>
                    </IonItem>
                    <IonItem>
                        <IonLabel position="stacked">Current User Id</IonLabel>
                        <IonInput readonly value={props.farmUserId}></IonInput>
                    </IonItem>
                    <IonItem>
                        <IonLabel position="stacked">New User Id</IonLabel>
                        <IonInput readonly value={newFarmUserId} placeholder="Select User Id" onClick={() => setShowSelectUserModal(true)}></IonInput>
                    </IonItem>
                    <IonItem lines="none">
                        <IonLabel className="ion-text-wrap" color="medium">
                            The farm and {plotsText}, along with their devices and plans, will move to the new user.
                        </IonLabel>
                    </IonItem>
                </IonList>
                <IonGrid>
                    <IonRow>
                        <IonCol><IonButton className="shiftRight" disabled={submitting} onClick={onSubmit}>Transfer</IonButton></IonCol>
                        <IonCol><IonButton onClick={() => props.onClose(false)}>Close</IonButton></IonCol>
                    </IonRow>
                </IonGrid>

                {showSelectUserModal && <IonModal isOpen={showSelectUserModal} >
                    <UserList
                        selectedValue={newFarmUserId}
                        onClickHandler={selectUser}
                        onClose={setShowSelectUserModal}
                    />
                </IonModal>}

                <IonAlert
                    isOpen={showConfirmation}
                    onDidDismiss={() => setShowConfirmation(false)}
                    header={'Confirm!'}
                    message={`Transfer farm ${props.farmId} and ${plotsText} from ${props.farmUserId} to ${newFarmUserId}?`}
                    buttons={[
                        {
                            text: 'No',
                            role: 'cancel',
                            cssClass: 'secondary'
                        },
                        {
                            text: 'Yes',
                            handler: () => {
                                transfer();
                            }
                        }
                    ]}
                />
            </IonContent>

            <IonToast
                isOpen={!!onError}
                onDidDismiss={() => setOnError('')}
                message={onError}
                duration={2000}
                color="danger"
            />
        </IonPage>
    );
};

export default TransferFarm;
